/**
 * Unified per-user admin view — Benutzer + Abrechnung + Verbrauch + Rechnungen
 * in a tabbed page so the admin doesn't need to bounce between three top-level
 * menu items to see one customer's full picture.
 */
import * as React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Loader2, Settings as SettingsIcon } from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import { useAdminUserBilling, useAdminSwitchUserPlan } from '../../hooks/useBilling';
import { useSetUserDisabled } from '../../hooks/useAdminUsers';
import { useToast } from '../../components/ui/toast';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { HeaderActions } from '../../components/HeaderActions';
import { AdminUserUsagePage } from './UserUsagePage';
import { AdminBillingDetailPage } from './BillingOversightPage';
import { AdminUserInvoicesTab } from './UserInvoicesTab';

type Tab = 'overview' | 'billing' | 'usage' | 'invoices';

export function AdminUserDetailPage() {
  const { uid } = useParams<{ uid: string }>();
  const navigate = useNavigate();
  const [tab, setTab] = React.useState<Tab>('overview');
  const billing = useAdminUserBilling(uid);

  if (!uid) {
    return (
      <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
        Keine Benutzer-ID übergeben.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <HeaderActions>
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin/users')}>
          <ArrowLeft className="mr-1.5 h-4 w-4" /> Zurück
        </Button>
        <Button asChild variant="outline" size="sm">
          <Link to={`/admin/users/${uid}/settings/provider`}>
            <SettingsIcon className="mr-1.5 h-3.5 w-3.5" /> Einstellungen verwalten
          </Link>
        </Button>
      </HeaderActions>

      <UserHeaderCard uid={uid} billing={billing.data?.user} />

      <Tabs value={tab} onValueChange={(v) => setTab(v as Tab)}>
        <TabsList>
          <TabsTrigger value="overview">Übersicht</TabsTrigger>
          <TabsTrigger value="billing">Abrechnung</TabsTrigger>
          <TabsTrigger value="usage">Verbrauch</TabsTrigger>
          <TabsTrigger value="invoices">Rechnungen</TabsTrigger>
        </TabsList>
        <TabsContent value="overview" className="mt-4">
          <OverviewTab uid={uid} />
        </TabsContent>
        <TabsContent value="billing" className="mt-4">
          {/* Re-uses the existing detail page so we don't duplicate code. */}
          <AdminBillingDetailPage />
        </TabsContent>
        <TabsContent value="usage" className="mt-4">
          <AdminUserUsagePage />
        </TabsContent>
        <TabsContent value="invoices" className="mt-4">
          <AdminUserInvoicesTab uid={uid} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function UserHeaderCard({
  uid,
  billing,
}: {
  uid: string;
  billing:
    | {
        firstName: string;
        lastName: string;
        displayName: string;
        email: string;
        role: string;
      }
    | undefined;
}) {
  if (!billing) {
    return (
      <Card>
        <CardContent className="py-4 text-sm text-muted-foreground">
          <Loader2 className="mr-2 inline h-3.5 w-3.5 animate-spin" /> Lade Benutzer …
        </CardContent>
      </Card>
    );
  }
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <CardTitle>
              {[billing.firstName, billing.lastName].filter(Boolean).join(' ') ||
                billing.displayName ||
                billing.email}
            </CardTitle>
            <CardDescription>{billing.email}</CardDescription>
          </div>
          <div className="flex gap-1">
            {billing.role === 'admin' && <Badge variant="warning">Admin</Badge>}
            <Badge variant="muted">
              <code className="font-mono text-[10px]">{uid}</code>
            </Badge>
          </div>
        </div>
      </CardHeader>
    </Card>
  );
}

function OverviewTab({ uid }: { uid: string }) {
  return (
    <div className="space-y-3">
      <AdminActionsCard uid={uid} />
      <Card>
        <CardHeader>
          <CardTitle>Schnellzugriff</CardTitle>
          <CardDescription>
            Direkter Sprung zu den wichtigsten Bereichen rund um diesen Benutzer.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-2 sm:grid-cols-3">
            <QuickLink
              label="Anrufe"
              to={`/admin/users/${uid}/settings/calls`}
              hint="Anruf-Log + Timeline pro Call"
            />
            <QuickLink
              label="Telefon-Konfiguration"
              to={`/admin/users/${uid}/settings/provider`}
              hint="Provider, KI, Stimme, Filler"
            />
            <QuickLink
              label="Tickets"
              to={`/admin/users/${uid}/settings/tickets`}
              hint="Service-Desk-Konfiguration"
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Power-tools an admin needs to manage a user without going to the
 * Firebase Console: suspend (Auth-disable) and plan-switch (force the
 * user onto/off a tier). Both routes through their respective backend
 * callables and the existing audit log captures the actor.
 */
function AdminActionsCard({ uid }: { uid: string }) {
  const setDisabled = useSetUserDisabled();
  const switchPlan = useAdminSwitchUserPlan();
  const { toast } = useToast();
  const [target, setTarget] = React.useState<
    'subscription' | 'payg' | 'enterprise' | 'ultimate' | 'cancel'
  >('subscription');
  const [confirmSuspend, setConfirmSuspend] = React.useState<null | boolean>(null);

  async function onToggleSuspend(newDisabled: boolean) {
    try {
      await setDisabled.mutateAsync({ uid, disabled: newDisabled });
      toast({
        title: newDisabled ? 'Account gesperrt' : 'Account entsperrt',
        description: newDisabled
          ? 'Der User kann sich bis zur Entsperrung nicht mehr einloggen.'
          : 'Login wieder freigegeben.',
      });
    } catch (err) {
      toast({
        title: 'Fehler',
        description: (err as Error).message,
        variant: 'destructive',
      });
    } finally {
      setConfirmSuspend(null);
    }
  }

  async function onSwitchPlan() {
    if (!confirm(`Plan auf "${target}" umstellen? Wirkt sofort.`)) return;
    try {
      await switchPlan.mutateAsync({ uid, target });
      toast({ title: 'Plan umgestellt', description: `Neuer Status: ${target}` });
    } catch (err) {
      toast({
        title: 'Fehler',
        description: (err as Error).message,
        variant: 'destructive',
      });
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Admin-Aktionen</CardTitle>
        <CardDescription>
          Account-Status + Plan überschreiben — wirken sofort, kein Mollie-Zyklus
          dazwischen. Audit-Log greift weiterhin.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-3 lg:grid-cols-2">
        <div className="space-y-2 rounded-md border border-border p-3">
          <div className="text-sm font-medium">Account-Status</div>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="destructive"
              onClick={() => setConfirmSuspend(true)}
              disabled={setDisabled.isPending}
            >
              {setDisabled.isPending && (
                <Loader2 className="mr-2 size-3.5 animate-spin" />
              )}
              Account sperren
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setConfirmSuspend(false)}
              disabled={setDisabled.isPending}
            >
              Entsperren
            </Button>
          </div>
          <p className="text-[11px] text-muted-foreground">
            Sperren = Firebase-Auth `disabled = true`. Existierende Sessions werden
            beim nächsten Token-Refresh ausgeloggt.
          </p>
        </div>
        <div className="space-y-2 rounded-md border border-border p-3">
          <div className="text-sm font-medium">Plan überschreiben</div>
          <div className="flex gap-2">
            <Select
              value={target}
              onValueChange={(v) => setTarget(v as typeof target)}
            >
              <SelectTrigger className="h-8 w-[180px] text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="subscription">Pro · Subscription</SelectItem>
                <SelectItem value="payg">Pay-as-you-go</SelectItem>
                <SelectItem value="enterprise">Enterprise</SelectItem>
                <SelectItem value="ultimate">Ultimate</SelectItem>
                <SelectItem value="cancel">Cancel (status='cancelled')</SelectItem>
              </SelectContent>
            </Select>
            <Button
              size="sm"
              onClick={() => void onSwitchPlan()}
              disabled={switchPlan.isPending}
            >
              {switchPlan.isPending && (
                <Loader2 className="mr-2 size-3.5 animate-spin" />
              )}
              Anwenden
            </Button>
          </div>
          <p className="text-[11px] text-muted-foreground">
            Enterprise/Ultimate: setzt `advancedAccess.tier` direkt — KEINE Mollie-
            Charge. Pro/PAYG: nur `mode` + `status` Flip im Billing-Doc.
          </p>
        </div>
      </CardContent>
      {confirmSuspend !== null && (
        <CardContent>
          <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-200">
            <div className="mb-2">
              {confirmSuspend
                ? 'Account wirklich sperren? User kann sich nicht mehr einloggen.'
                : 'Account wieder entsperren?'}
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant={confirmSuspend ? 'destructive' : 'default'}
                onClick={() => void onToggleSuspend(confirmSuspend)}
              >
                Bestätigen
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setConfirmSuspend(null)}
              >
                Abbrechen
              </Button>
            </div>
          </div>
        </CardContent>
      )}
    </Card>
  );
}

function QuickLink({
  label,
  to,
  hint,
}: {
  label: string;
  to: string;
  hint: string;
}) {
  return (
    <Button asChild variant="outline" className="h-auto justify-start py-3">
      <Link to={to}>
        <div className="flex w-full items-center gap-2 text-left">
          <div className="flex-1">
            <div className="text-sm font-medium">{label}</div>
            <div className="mt-0.5 text-xs text-muted-foreground">{hint}</div>
          </div>
          <ExternalLink className="h-3.5 w-3.5 text-muted-foreground" />
        </div>
      </Link>
    </Button>
  );
}
